import Link from '@/lib/link'
import { ContentPage, Prose, Section } from '@/components/content-page'
import { MobileGuide } from '@/components/mobile-guide'

const checklist = [
  'Your radio and a charged battery',
  'A Bluetooth-capable radio or the correct programming cable for your model',
  'A Windows, macOS, or Linux computer — or an Android 8+ phone',
  'A list of the frequencies you want to program',
]

export default function BeginnersGuidePage() {
  return (
    <ContentPage
      eyebrow="// beginners guide"
      title="Your first time with NewChirp"
      description="Never programmed a radio before? Start with the desktop workflow or follow the Android app screen by screen. You do not need to be a software engineer."
      wide
    >
      <MobileGuide variant="beginner" />
      <div className="mx-auto max-w-4xl pt-10">
        <Prose>
        <Section title="What you'll need">
          <ul className="flex flex-col gap-2">
            {checklist.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="1. Understand what programming means">
          <p>
            Your radio stores channels in its memory — each with a frequency, a name, and a few
            settings like tone and power. &quot;Programming&quot; simply means editing that memory.
            NewChirp reads the memory onto your computer, lets you edit it in a familiar spreadsheet
            view, and writes it back.
          </p>
        </Section>

        <Section title="2. Connect and download first">
          <p>
            Always start by downloading the current memory from your radio, even on a brand-new
            device. This gives NewChirp the exact layout your model expects, and it gives you a safe
            backup to fall back on. Plug in the cable, power on the radio, and choose{' '}
            <span className="font-mono text-foreground">Radio → Download From Radio</span>.
          </p>
        </Section>

        <Section title="3. Add your channels">
          <p>
            Double-click an empty row and type in a frequency. Give it a short name, set the tone if
            your local repeater requires one, and pick a power level. Repeat for each channel. If
            you make a mistake, editing is instant and nothing is written to the radio yet.
          </p>
        </Section>

        <Section title="4. Upload when you're ready">
          <p>
            Choose <span className="font-mono text-foreground">Radio → Upload To Radio</span> to
            write your channels back to the device. That&apos;s it — disconnect the cable and your
            radio is programmed.
          </p>
        </Section>

        <Section title="Common first-timer tips">
          <p>
            Take your time and only upload once you have double-checked the frequencies. Keep your
            backup image somewhere safe. And if the radio is not detected, the cable is almost
            always the culprit — see the{' '}
            <Link href="/faq" className="text-primary underline underline-offset-4">
              FAQ
            </Link>{' '}
            for driver help.
          </p>
        </Section>
        </Prose>
      </div>
    </ContentPage>
  )
}
